/**
 * Utility functions for error handling and user-friendly error messages
 */

/**
 * Error codes and their user-friendly messages
 */
const ERROR_MESSAGES: Record<string, string> = {
  // File validation errors
  'FILE_TOO_LARGE': 'The file is too large. Maximum file size is 10MB.',
  'INVALID_FILE_TYPE': 'Invalid file type. Only CSV files are allowed.',
  'EMPTY_FILE': 'The file is empty. Please upload a file with content.',
  
  // API errors
  'UNAUTHORIZED': 'You are not authorized to perform this action. Please log in again.',
  'FORBIDDEN': 'You do not have permission to access this resource.',
  'NOT_FOUND': 'The requested resource was not found.',
  'INTERNAL_SERVER_ERROR': 'An internal server error occurred. Please try again later.',
  
  // Configuration errors
  'INVALID_CONFIGURATION': 'The configuration file contains invalid data.',
  'DUPLICATE_CONFIGURATION': 'A configuration with the same ID already exists.',
  'MISSING_REQUIRED_FIELDS': 'The configuration is missing required fields.',
  
  // S3 errors
  'S3_UPLOAD_FAILED': 'Failed to upload file to S3. Please try again.',
  'S3_DOWNLOAD_FAILED': 'Failed to download file from S3. Please try again.',
  
  // Lambda errors
  'LAMBDA_INVOCATION_FAILED': 'Failed to invoke Lambda function. Please try again.',
  'LAMBDA_TIMEOUT': 'The Lambda function timed out. Please try again.',
  
  // Default error
  'DEFAULT': 'An unexpected error occurred. Please try again or contact support.'
};

/**
 * Get a user-friendly error message based on an error code or message
 * 
 * @param errorCodeOrMessage - The error code or message
 * @returns A user-friendly error message
 */
export const getUserFriendlyErrorMessage = (errorCodeOrMessage: string): string => {
  // Check if we have a specific message for this error code
  if (ERROR_MESSAGES[errorCodeOrMessage]) {
    return ERROR_MESSAGES[errorCodeOrMessage];
  }
  
  // Check if the error message contains any known error codes
  for (const [code, message] of Object.entries(ERROR_MESSAGES)) {
    if (errorCodeOrMessage.includes(code)) {
      return message;
    }
  }
  
  // Handle specific error patterns
  if (errorCodeOrMessage.toLowerCase().includes('timeout')) {
    return 'The request timed out. Please try again.';
  }
  
  if (errorCodeOrMessage.toLowerCase().includes('network')) {
    return 'A network error occurred. Please check your connection and try again.';
  }
  
  if (errorCodeOrMessage.toLowerCase().includes('permission')) {
    return 'You do not have permission to perform this action.';
  }
  
  // Return the original error message if we don't have a specific user-friendly message
  return errorCodeOrMessage;
};

/**
 * Format an error object into a user-friendly error message
 * 
 * @param error - The error object
 * @returns A user-friendly error message
 */
export const formatError = (error: unknown): string => {
  if (typeof error === 'string') {
    return getUserFriendlyErrorMessage(error);
  }
  
  if (error instanceof Error) {
    return getUserFriendlyErrorMessage(error.message);
  }
  
  return getUserFriendlyErrorMessage('DEFAULT');
};

/**
 * Parse API error response and return user-friendly error message(s)
 * 
 * @param response - The API response object
 * @param returnArray - Whether to return an array of error messages (default: false)
 * @returns A user-friendly error message or array of messages
 */
export const parseApiError = (response: any, returnArray: boolean = false): string | string[] => {
  // Check for errors array first (new format)
  if (response?.data?.errors && Array.isArray(response.data.errors) && response.data.errors.length > 0) {
    const errorsArray = response.data.errors.map((err: string) => getUserFriendlyErrorMessage(err));
    return returnArray ? errorsArray : errorsArray[0];
  }
  
  // Check if the response has an error message
  if (response?.data?.message) {
    const message = getUserFriendlyErrorMessage(response.data.message);
    return returnArray ? [message] : message;
  }
  
  // Check if the response has an error code
  if (response?.data?.code) {
    const message = getUserFriendlyErrorMessage(response.data.code);
    return returnArray ? [message] : message;
  }
  
  // Check if the response has a status code
  if (response?.status) {
    let message;
    switch (response.status) {
      case 400:
        message = 'Bad request. Please check your input and try again.';
        break;
      case 401:
        message = 'You are not authorized to perform this action. Please log in again.';
        break;
      case 403:
        message = 'You do not have permission to access this resource.';
        break;
      case 404:
        message = 'The requested resource was not found. Please cut us a SIM.';
        break;
      case 500:
        message = 'An internal server error occurred. Please cut us a SIM.';
        break;
      default:
        message = `Server returned error code ${response.status}. Please try again. Please cut us a SIM.`;
        break;
    }
    return returnArray ? [message] : message;
  }
  
  const defaultMessage = getUserFriendlyErrorMessage('DEFAULT');
  return returnArray ? [defaultMessage] : defaultMessage;
};
